import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Button, Text } from '@/components/ui';
import {
  createCategory,
  fetchAccounts,
  fetchCategories,
  fetchCurrencies,
  importTransactions,
  type TransactionInput,
} from '@/lib/finance/api';
import { categoryOptions } from '@/lib/finance/format';
import {
  IMPORT_FIELDS,
  IMPORT_FIELD_LABELS,
  REQUIRED_IMPORT_FIELDS,
  autoMapColumns,
  buildResolvedRows,
  distinctUnresolved,
  effectiveLookups,
  parseCsv,
  splitCategoryLabel,
  type ColumnMapping,
  type ImportField,
  type ImportLookups,
  type NameAction,
} from '@/lib/finance/import';
import type { Account, Category, Currency, FinanceCategoryKind } from '@/lib/finance/types';

type Step = 'upload' | 'map' | 'review' | 'done';

const PREVIEW_ROWS = 50;

const SAMPLE = `Date,Category,Account,Currency,Amount,Notes
2026-06-03,Food - Groceries,Ineco Chequing,AMD,18500,SAS market
2026-06-04,Housing - Rent/Mortgage,Cash,USD,1200,June rent`;

export function ImportWizard() {
  const router = useRouter();

  const [step, setStep] = useState<Step>('upload');
  const [rawText, setRawText] = useState('');
  const [parsed, setParsed] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [masterLoading, setMasterLoading] = useState(true);

  const [accountActions, setAccountActions] = useState<Map<string, NameAction>>(new Map());
  const [categoryActions, setCategoryActions] = useState<Map<string, NameAction>>(new Map());

  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setMasterLoading(true);
      try {
        const [accountRows, categoryRows, currencyRows] = await Promise.all([
          fetchAccounts(),
          fetchCategories(),
          fetchCurrencies(),
        ]);
        if (cancelled) return;
        setAccounts(accountRows);
        setCategories(categoryRows);
        setCurrencies(currencyRows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load master data');
      } finally {
        if (!cancelled) setMasterLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryOpts = useMemo(() => categoryOptions(categories), [categories]);

  /** Lookups from Master Data alone — used to discover which names are unmatched. */
  const baseLookups = useMemo<ImportLookups>(() => {
    const currencyCodes = new Set(currencies.map((c) => c.code.toUpperCase()));
    const accountsByName = new Map<string, number>();
    for (const a of accounts) accountsByName.set(a.name.toLowerCase(), a.id);
    const categoryByLabel = new Map<string, { id: number; kind: Category['kind'] }>();
    for (const o of categoryOpts)
      categoryByLabel.set(o.label.toLowerCase(), { id: o.id, kind: o.kind });
    return { currencyCodes, accountsByName, categoryByLabel };
  }, [accounts, categoryOpts, currencies]);

  const unresolved = useMemo(() => {
    if (!parsed) return { accounts: [], categories: [] };
    return distinctUnresolved(buildResolvedRows(parsed.rows, mapping, baseLookups));
  }, [parsed, mapping, baseLookups]);

  const categoryKindById = useMemo(() => {
    const map = new Map<number, FinanceCategoryKind>();
    for (const o of categoryOpts) map.set(o.id, o.kind);
    return map;
  }, [categoryOpts]);

  /**
   * Effective lookups: Master Data plus each unmatched name's chosen action, with
   * defaults applied (accounts → blank, categories → create) so the review counts
   * and the Import button reflect what will happen without requiring a click.
   */
  const lookups = useMemo<ImportLookups>(
    () =>
      effectiveLookups(
        baseLookups,
        unresolved,
        { accounts: accountActions, categories: categoryActions },
        categoryKindById,
      ),
    [baseLookups, unresolved, accountActions, categoryActions, categoryKindById],
  );

  const resolved = useMemo(() => {
    if (!parsed) return [];
    return buildResolvedRows(parsed.rows, mapping, lookups);
  }, [parsed, mapping, lookups]);
  const readyCount = resolved.filter((r) => r.errors.length === 0).length;
  const errorCount = resolved.length - readyCount;

  const requiredMapped = REQUIRED_IMPORT_FIELDS.every((f) => mapping[f] != null);

  function onParse() {
    setError(null);
    const result = parseCsv(rawText);
    if (result.headers.length === 0 || result.rows.length === 0) {
      setError('No data rows found. Paste CSV with a header row and at least one record.');
      return;
    }
    setParsed(result);
    setMapping(autoMapColumns(result.headers));
    setAccountActions(new Map());
    setCategoryActions(new Map());
    setStep('map');
  }

  async function onFile(file: File) {
    setRawText(await file.text());
  }

  function setFieldColumn(field: ImportField, value: string) {
    setMapping((prev) => {
      const next = { ...prev };
      if (value === '') delete next[field];
      else next[field] = Number(value);
      return next;
    });
  }

  function accountActionFor(key: string): NameAction {
    return accountActions.get(key) ?? 'blank';
  }
  function categoryActionFor(key: string): NameAction {
    return categoryActions.get(key) ?? 'create';
  }

  /** Create the categories the user chose to `create`, returning lower-label → {id,kind}. */
  async function ensureCreatedCategories(): Promise<
    Map<string, { id: number; kind: Category['kind'] }>
  > {
    const created = new Map<string, { id: number; kind: Category['kind'] }>();
    let pool = [...categories];

    const findTop = (name: string) =>
      pool.find((c) => c.parentId == null && c.name.toLowerCase() === name.toLowerCase());
    const findChild = (parentId: number, name: string) =>
      pool.find((c) => c.parentId === parentId && c.name.toLowerCase() === name.toLowerCase());

    for (const label of unresolved.categories) {
      if (categoryActionFor(label.toLowerCase()) !== 'create') continue;
      const { parent, child } = splitCategoryLabel(label);

      let parentRow = findTop(parent);
      if (!parentRow) {
        parentRow = await createCategory({ name: parent, kind: 'expense' });
        pool = [...pool, parentRow];
      }
      let target = parentRow;
      if (child) {
        let childRow = findChild(parentRow.id, child);
        if (!childRow) {
          childRow = await createCategory({ name: child, parentId: parentRow.id, kind: 'expense' });
          pool = [...pool, childRow];
        }
        target = childRow;
      }
      created.set(label.toLowerCase(), { id: target.id, kind: target.kind });
    }

    setCategories(pool);
    return created;
  }

  async function onImport() {
    setImporting(true);
    setError(null);
    try {
      const created = await ensureCreatedCategories();

      // Rebuild lookups with the real ids from any just-created categories.
      const finalLookups: ImportLookups = {
        ...lookups,
        categoryByLabel: new Map(lookups.categoryByLabel),
      };
      for (const [key, value] of created) finalLookups.categoryByLabel.set(key, value);

      const finalRows = buildResolvedRows(parsed!.rows, mapping, finalLookups);
      const ready = finalRows.filter((r) => r.errors.length === 0);
      const payload: TransactionInput[] = ready.map((r) => ({
        date: r.date!,
        accountId: r.accountId,
        toAccountId: r.toAccountId,
        categoryId: r.categoryId,
        currencyCode: r.currencyCode!,
        amount: r.amount!,
        notes: r.notes,
      }));

      const imported = payload.length > 0 ? await importTransactions(payload) : 0;
      setImportedCount(imported);
      setSkippedCount(finalRows.length - imported);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  return (
    <View className="max-w-3xl">
      <View className="mb-4 flex-row items-center gap-2">
        <Text variant="h2">Import transactions</Text>
        <View className="flex-1" />
        <Text variant="body-sm" className="text-muted">
          {step === 'upload'
            ? 'Step 1 of 3 · Upload'
            : step === 'map'
              ? 'Step 2 of 3 · Map columns'
              : step === 'review'
                ? 'Step 3 of 3 · Review'
                : 'Done'}
        </Text>
      </View>

      {error ? (
        <Text variant="body-sm" className="mb-3 text-danger">
          {error}
        </Text>
      ) : null}

      {step === 'upload' ? (
        <View>
          <Text variant="body-sm" className="mb-2 text-muted">
            Paste straight from Google Sheets (tab-separated) or paste CSV — a header row plus one
            row per transaction — or choose a `.csv` file. You map the columns next, so column order
            doesn’t matter.
          </Text>
          <input
            type="file"
            accept=".csv,text/csv"
            className="mb-3 block text-sm"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFile(file);
            }}
          />
          <textarea
            className="cs-input h-56 w-full font-mono text-xs"
            placeholder={SAMPLE}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
          />
          <View className="mt-3 flex-row gap-2">
            <Button label="Parse CSV" variant="primary" size="sm" onPress={onParse} />
            <Button
              label="Use sample"
              variant="ghost"
              size="sm"
              onPress={() => setRawText(SAMPLE)}
            />
            <View className="flex-1" />
            <Button
              label="Cancel"
              variant="ghost"
              size="sm"
              onPress={() => router.push('/finance/transactions')}
            />
          </View>
        </View>
      ) : null}

      {step === 'map' && parsed ? (
        <View>
          <Text variant="body-sm" className="mb-3 text-muted">
            Match each transaction field to a column. Date, Currency and Amount are required.
          </Text>
          <div className="grid grid-cols-2 gap-3">
            {IMPORT_FIELDS.map((field) => (
              <label key={field} className="flex flex-col gap-1">
                <span className="cs-text-label">
                  {IMPORT_FIELD_LABELS[field]}
                  {REQUIRED_IMPORT_FIELDS.includes(field) ? ' *' : ''}
                </span>
                <select
                  className="cs-input h-9 py-0"
                  value={mapping[field] != null ? String(mapping[field]) : ''}
                  onChange={(e) => setFieldColumn(field, e.target.value)}
                >
                  <option value="">— Not mapped —</option>
                  {parsed.headers.map((header, index) => (
                    <option key={index} value={String(index)}>
                      {header || `Column ${index + 1}`}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          <Text variant="body-sm" className="mb-1 mt-4 font-medium">
            Preview ({parsed.rows.length} rows)
          </Text>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-xs">
              <thead>
                <tr className="text-fg-muted">
                  {IMPORT_FIELDS.map((field) =>
                    mapping[field] != null ? (
                      <th key={field} className="border-b border-border px-2 py-1 text-left">
                        {IMPORT_FIELD_LABELS[field]}
                      </th>
                    ) : null,
                  )}
                </tr>
              </thead>
              <tbody>
                {parsed.rows.slice(0, 5).map((row, i) => (
                  <tr key={i}>
                    {IMPORT_FIELDS.map((field) =>
                      mapping[field] != null ? (
                        <td key={field} className="border-b border-border px-2 py-1">
                          {row[mapping[field]!] ?? ''}
                        </td>
                      ) : null,
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <View className="mt-4 flex-row gap-2">
            <Button label="Back" variant="ghost" size="sm" onPress={() => setStep('upload')} />
            <View className="flex-1" />
            <Button
              label="Next: review"
              variant="primary"
              size="sm"
              disabled={!requiredMapped}
              onPress={() => setStep('review')}
            />
          </View>
          {!requiredMapped ? (
            <Text variant="caption" className="mt-1 text-fg-muted">
              Map Date, Currency and Amount to continue.
            </Text>
          ) : null}
        </View>
      ) : null}

      {step === 'review' ? (
        masterLoading ? (
          <ActivityIndicator />
        ) : (
          <View>
            <View className="mb-3 flex-row flex-wrap gap-x-6 gap-y-1">
              <Text variant="body-sm">
                Ready:{' '}
                <Text variant="body-sm" className="font-semibold text-success">
                  {readyCount}
                </Text>
              </Text>
              <Text variant="body-sm">
                Skipped (errors):{' '}
                <Text
                  variant="body-sm"
                  className={errorCount > 0 ? 'font-semibold text-fg-muted' : 'font-semibold'}
                >
                  {errorCount}
                </Text>
              </Text>
            </View>

            {unresolved.accounts.length > 0 || unresolved.categories.length > 0 ? (
              <View className="mb-4 rounded-lg border border-border p-3">
                <Text variant="body-sm" className="mb-2 font-medium">
                  Resolve unmatched names
                </Text>
                {unresolved.accounts.map((name) => {
                  const key = name.toLowerCase();
                  return (
                    <div key={`acc:${key}`} className="mb-2 flex items-center gap-2">
                      <span className="w-48 truncate text-sm">
                        Account: <span className="font-medium">{name}</span>
                      </span>
                      <select
                        className="cs-input h-8 py-0"
                        value={accountActionFor(key)}
                        onChange={(e) =>
                          setAccountActions((prev) => new Map(prev).set(key, e.target.value))
                        }
                      >
                        <option value="blank">Leave blank</option>
                        {accounts.map((a) => (
                          <option key={a.id} value={String(a.id)}>
                            Map to: {a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
                {unresolved.categories.map((label) => {
                  const key = label.toLowerCase();
                  return (
                    <div key={`cat:${key}`} className="mb-2 flex items-center gap-2">
                      <span className="w-48 truncate text-sm">
                        Category: <span className="font-medium">{label}</span>
                      </span>
                      <select
                        className="cs-input h-8 py-0"
                        value={categoryActionFor(key)}
                        onChange={(e) =>
                          setCategoryActions((prev) => new Map(prev).set(key, e.target.value))
                        }
                      >
                        <option value="create">Create “{label}”</option>
                        <option value="blank">Leave uncategorized</option>
                        {categoryOpts.map((o) => (
                          <option key={o.id} value={String(o.id)}>
                            Map to: {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
                <Text variant="caption" className="text-muted">
                  New categories are created as top-level/expense (with `Parent - Child` split);
                  adjust kind later in Master Data.
                </Text>
              </View>
            ) : null}

            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-xs">
                <thead>
                  <tr className="text-fg-muted">
                    <th className="border-b border-border px-2 py-1 text-left">#</th>
                    <th className="border-b border-border px-2 py-1 text-left">Date</th>
                    <th className="border-b border-border px-2 py-1 text-left">Account</th>
                    <th className="border-b border-border px-2 py-1 text-left">Category</th>
                    <th className="border-b border-border px-2 py-1 text-left">Cur</th>
                    <th className="border-b border-border px-2 py-1 text-right">Amount</th>
                    <th className="border-b border-border px-2 py-1 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {resolved.slice(0, PREVIEW_ROWS).map((r) => (
                    <tr key={r.index} className={r.errors.length > 0 ? 'bg-warning-muted' : ''}>
                      <td className="border-b border-border px-2 py-1 text-fg-subtle">
                        {r.index + 1}
                      </td>
                      <td className="border-b border-border px-2 py-1">{r.date ?? '—'}</td>
                      <td className="border-b border-border px-2 py-1">{r.accountName || '—'}</td>
                      <td className="border-b border-border px-2 py-1">{r.categoryLabel || '—'}</td>
                      <td className="border-b border-border px-2 py-1">{r.currencyCode ?? '—'}</td>
                      <td className="border-b border-border px-2 py-1 text-right tabular-nums">
                        {r.amount ?? '—'}
                      </td>
                      <td className="border-b border-border px-2 py-1">
                        {r.errors.length === 0 ? (
                          <span className="text-success">ready</span>
                        ) : (
                          <span className="text-fg-muted" title={r.errors.join('; ')}>
                            {r.errors[0]}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {resolved.length > PREVIEW_ROWS ? (
              <Text variant="caption" className="mt-1 text-muted">
                Showing first {PREVIEW_ROWS} of {resolved.length} rows.
              </Text>
            ) : null}

            <View className="mt-4 flex-row gap-2">
              <Button label="Back" variant="ghost" size="sm" onPress={() => setStep('map')} />
              <View className="flex-1" />
              <Button
                label={
                  importing
                    ? 'Importing…'
                    : `Import ${readyCount} row${readyCount === 1 ? '' : 's'}`
                }
                variant="primary"
                size="sm"
                disabled={importing || readyCount === 0}
                onPress={() => void onImport()}
              />
            </View>
          </View>
        )
      ) : null}

      {step === 'done' ? (
        <View>
          <Text variant="body" className="mb-1">
            Imported{' '}
            <Text variant="body" className="font-semibold text-success">
              {importedCount}
            </Text>{' '}
            transaction{importedCount === 1 ? '' : 's'}.
            {skippedCount > 0 ? ` ${skippedCount} row(s) were skipped due to errors.` : ''}
          </Text>
          <Text variant="body-sm" className="mb-4 text-muted">
            USD values are computed from each month’s FX rate — fill any missing rates in Master
            Data → FX Rates.
          </Text>
          <View className="flex-row gap-2">
            <Button
              label="View transactions"
              variant="primary"
              size="sm"
              onPress={() => router.push('/finance/transactions')}
            />
            <Button
              label="Import another file"
              variant="secondary"
              size="sm"
              onPress={() => {
                setStep('upload');
                setParsed(null);
                setRawText('');
                setImportedCount(0);
                setSkippedCount(0);
              }}
            />
          </View>
        </View>
      ) : null}
    </View>
  );
}
